import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import Spinner from 'react-bootstrap/Spinner';
import Cookies from 'js-cookie';
import { useSearchParams } from 'react-router-dom';

// Import all settings-related components
import Home from './Home'; // Google & Meta Integration
import Groups from './Groups';
import LocationPersonalization from './LocationPersonalization';
import SavingCalculator from './SavingCalculator';
import CreativeAnalysisSetting from './CreativeAnalysisSetting';
import Setup from './Setup';
import SignOut from './SignOut';
import BlockRepository from './BlockRepository';
import AssetGroups from './AssetGroups';
import SectionTags from './SectionTags';
import DomainsAndUrls from './DomainsAndUrls';
import BusinessContextAndAssets from './BusinessContextAndAssets';
import SubscriptionManagement from './SubscriptionManagement';
import YoutubeIntegration from './YoutubeIntegration';
import SupportTab from './SupportTab';
import FormSettings from './FormSettings';
import TeamWorkspaceSettings from './TeamWorkspaceSettings';
import McpServerSettings from './McpServerSettings';
import AppearanceSettings from './AppearanceSettings';
import { MCP_PAGE_ID } from '../utils/pageRoutes';

// Every section id is hand-synced across FIVE structures, all exported so a
// test can read the real ones (tests/unit/client/pages/Settings.mcpSection.test.js):
// VALID_TAB_IDS, TAB_GROUP, SECTION_PAGE_ID, ALWAYS_INCLUDED_IDS and
// settingsSections. Missing one gives a tab that renders but cannot be
// deep-linked, or the reverse.
export const VALID_TAB_IDS = ['google-meta-integration', 'domains-and-urls', 'business-context-and-assets', 'youtube-integration', 'location-personalization', 'saving-calculator', 'meta-creative-settings', 'block-repository', 'asset-groups', 'section-tags', 'groups', 'setup', 'billing-payment', 'support', 'form-settings', 'sign-out', 'workspace', 'teams', 'mcp', 'appearance'];

// Settings is grouped into four sidebar entries. Each tab id maps to one group.
export const TAB_GROUP = {
  'google-meta-integration': 'integrations',
  'domains-and-urls': 'assets',
  'business-context-and-assets': 'assets',
  'youtube-integration': 'assets',
  'location-personalization': 'assets',
  'meta-creative-settings': 'assets',
  'block-repository': 'assets',
  'asset-groups': 'assets',
  'section-tags': 'assets',
  'setup': 'assets',
  'form-settings': 'assets',
  'saving-calculator': 'general',
  'groups': 'general',
  'billing-payment': 'general',
  'support': 'general',
  'sign-out': 'general',
  'appearance': 'general',
  'workspace': 'teams-workspace',
  'teams': 'teams-workspace',
  'mcp': 'integrations',
};
const groupOf = (tabId) => TAB_GROUP[tabId] || 'assets';

// The group a /settings?tab=… URL opens on — what this page shows and what
// the app sidebar highlights (Sidebar.jsx reads this rather than keeping its
// own copy of TAB_GROUP, which once lacked `mcp` and lit up Assets for it).
export function settingsGroupForTab(tabFromUrl) {
  return groupOf(tabFromUrl && VALID_TAB_IDS.includes(tabFromUrl) ? tabFromUrl : 'google-meta-integration');
}

// Sidebar group order (used to fall back to the first accessible group).
const GROUP_ORDER = ['general', 'assets', 'integrations', 'teams-workspace'];

// id → permission page id (mirrors settingsSections' pageId — keep in sync).
// `mcp` is gated on the MCP page permission (the same page the server requires
// to consent to, and to call, an MCP connection) — MCP_PAGE_ID, never a literal:
// tests/unit/server/services/mcpPageId.test.js fails on any other registration
// of the number.
export const SECTION_PAGE_ID = {
  'google-meta-integration': 4, 'domains-and-urls': 44, 'business-context-and-assets': 45,
  'youtube-integration': 46, 'location-personalization': 26, 'meta-creative-settings': 17,
  'block-repository': 33, 'asset-groups': 56, 'section-tags': 42, 'setup': 32, 'form-settings': 44,
  'saving-calculator': 22, 'groups': 21, 'billing-payment': 103, 'support': null, 'sign-out': 23,
  'workspace': null, 'teams': null, 'mcp': MCP_PAGE_ID, 'appearance': null,
};

// Sections whose tab shows even without an explicit page permission.
// Deliberately contains NO assets-group section: Assets tabs render strictly
// from allowed_pages grants (2026-09 request — the ungated defaults confused
// admins). `workspace`/`teams` have pageId null and would be un-gateable
// without this list; the general group keeps its historical always-tabs.
// `appearance` (the per-person theme, 2026-09-24) is for everyone.
// `mcp` is deliberately NOT here: it is gated on its own page (spec 2026-09-21
// §12.2, retiring §7.2's always-visible workaround).
export const ALWAYS_INCLUDED_IDS = new Set([
  'sign-out', 'billing-payment', 'support', 'workspace', 'teams', 'appearance',
]);

// --- Settings Sections with Page IDs ---
// Module-level (it reads no props or state) so it can be exported alongside
// the other four structures.
//
// `mountWhenOpened`: the section mounts the first time its tab is opened,
// then stays mounted, instead of mounting with the Settings page. Every other
// section mounts with the page and is merely hidden while inactive — fine for
// those, but the MCP section fetches on mount, and that fetch calls the MCP
// Worker, so every Settings visit would otherwise reach the Worker for a
// section nobody opened.
export const settingsSections = [
  // Tab id stays 'google-meta-integration' for deep-link compat; the panel now
  // also hosts the Google Drive link, so the visible title is just Integrations.
  { id: 'google-meta-integration', title: 'Integrations', component: Home, pageId: 4, group: 'integrations' },
  { id: 'mcp', title: 'MCP', component: McpServerSettings, pageId: MCP_PAGE_ID, group: 'integrations', mountWhenOpened: true },
  { id: 'domains-and-urls', title: 'Domains & URLs', component: DomainsAndUrls, pageId: 44, group: 'assets' },
  { id: 'business-context-and-assets', title: 'Business Context', component: BusinessContextAndAssets, pageId: 45, group: 'assets' },
  { id: 'youtube-integration', title: 'YouTube Integration', component: YoutubeIntegration, pageId: 46, group: 'assets' },
  { id: 'location-personalization', title: 'Location Personalization', component: LocationPersonalization, pageId: 26, group: 'assets' },
  { id: 'meta-creative-settings', title: 'Meta Creative Settings', component: CreativeAnalysisSetting, pageId: 17, group: 'assets' },
  { id: 'block-repository', title: 'Assets', component: BlockRepository, pageId: 33, group: 'assets' },
  { id: 'asset-groups', title: 'Asset Groups', component: AssetGroups, pageId: 56, group: 'assets' },
  { id: 'section-tags', title: 'Section Tags', component: SectionTags, pageId: 42, group: 'assets' },
  { id: 'setup', title: 'Setup', component: Setup, pageId: 32, group: 'assets' },
  // { id: 'form-settings', title: 'Form Settings', component: FormSettings, pageId: 44, group: 'assets' },
  { id: 'saving-calculator', title: 'Saving Calculator', component: SavingCalculator, pageId: 22, group: 'general' },
  { id: 'groups', title: 'Groups', component: Groups, pageId: 21, group: 'general' },
  { id: 'billing-payment', title: 'Billing & Payment', component: SubscriptionManagement, pageId: 103, group: 'general' },
  // { id: 'support', title: 'Support', component: SupportTab, pageId: null, group: 'general' },
  { id: 'appearance', title: 'Appearance', component: AppearanceSettings, pageId: null, group: 'general' },
  { id: 'sign-out', title: 'Sign Out', component: SignOut, pageId: 23, group: 'general' },
  { id: 'workspace', title: 'Workspace', component: TeamWorkspaceSettings, pageId: null, group: 'teams-workspace' },
  { id: 'teams', title: 'Teams', component: TeamWorkspaceSettings, pageId: null, group: 'teams-workspace' },
];

const isSectionVisibleFor = (id, allowedPages = []) =>
  ALWAYS_INCLUDED_IDS.has(id) || allowedPages.includes(SECTION_PAGE_ID[id]);

// The store's page ids as Sidebar.jsx derives them from the
// `allowed_pages_json` cookie, or the [4, 5, 33] render fallback when the
// cookie has no entry for the store.
function readAllowedPagesForStore(shop) {
  let allowed_pages_json = {};
  try {
    allowed_pages_json = Cookies.get('allowed_pages_json') ? JSON.parse(Cookies.get('allowed_pages_json')) : {};
  } catch (e) {
    allowed_pages_json = {};
  }

  const shopKey = shop ? shop.replace('.myshopify.com', '') : '';

  if (
    allowed_pages_json &&
    shopKey &&
    Object.keys(allowed_pages_json).includes(shopKey) &&
    allowed_pages_json[shopKey]
  ) {
    return Object.values(allowed_pages_json[shopKey]);
  }
  return [4, 5, 33];
}

// Groups (sidebar entries) with at least one visible section for these page
// permissions — used to hide a Settings group the user can access none of.
export function visibleSettingsGroups(allowedPages = []) {
  const groups = new Set();
  for (const id of Object.keys(TAB_GROUP)) {
    if (isSectionVisibleFor(id, allowedPages)) groups.add(TAB_GROUP[id]);
  }
  return groups;
}

const Settings = ({ ViewedPage, shop_domain, app_url }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const t = tabFromUrl && VALID_TAB_IDS.includes(tabFromUrl) ? tabFromUrl : 'google-meta-integration';
    return t;
  });
  const [activePage, setActivePage] = useState(() => settingsGroupForTab(tabFromUrl));


  // Open tab from URL (e.g. /settings?tab=sign-out when clicking sidebar user profile)
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && VALID_TAB_IDS.includes(t)) {
      setActiveTab(t);
      setActivePage(groupOf(t));
    }
  }, [searchParams]);

  // Call ViewedPage only once when component mounts
  useEffect(() => {
    ViewedPage(30); // New page ID for the unified Settings page
  }, []);

  // Handle tab change (keep URL in sync for deep links e.g. /settings?tab=flows-setup)
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };


  // --- Allowed Pages Logic (copied from Sidebar.jsx pattern) ---
  const [currentShop, setCurrentShop] = useState(Cookies.get('shop'));
  // Read on the first render as well as in the effect: a section gated on its
  // own page (MCP) would otherwise first render against the [4, 5, 33]
  // fallback — hidden, with another tab shown in its place — until the effect ran.
  const [allowedPagesForStore, setAllowedPagesForStore] = useState(() => readAllowedPagesForStore(currentShop));

  useEffect(() => {
    setAllowedPagesForStore(readAllowedPagesForStore(currentShop));
  }, [currentShop]); // Removed shop_domain from dependencies

  // Filter sections by page permission (ALWAYS_INCLUDED_IDS show regardless).
  const filteredSections = settingsSections.filter((section) => isSectionVisibleFor(section.id, allowedPagesForStore));
  // Hide a group whose sections are all inaccessible: if the active group has no
  // visible section, fall back to the first accessible group (and its first tab).
  const visibleGroups = new Set(filteredSections.map((s) => s.group));
  const effectiveActivePage = visibleGroups.has(activePage) ? activePage : (GROUP_ORDER.find((g) => visibleGroups.has(g)) || activePage);
  const groupSections = filteredSections.filter((s) => s.group === effectiveActivePage);
  const effectiveActiveTab = groupSections.some((s) => s.id === activeTab) ? activeTab : (groupSections[0]?.id || activeTab);

  // Tabs opened at least once this visit — a `mountWhenOpened` section stays
  // mounted (keeping its state) after the user moves to another tab.
  const [openedTabs, setOpenedTabs] = useState(() => new Set());
  useEffect(() => {
    setOpenedTabs((prev) => (prev.has(effectiveActiveTab) ? prev : new Set(prev).add(effectiveActiveTab)));
  }, [effectiveActiveTab]);
  const isSectionMounted = (section) => (
    !section.mountWhenOpened || section.id === effectiveActiveTab || openedTabs.has(section.id)
  );

  const TAB_ICONS = {
    'google-meta-integration': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="tab-icon" aria-hidden="true">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      </svg>
    ),
    'location-personalization': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="tab-icon" aria-hidden="true">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
      </svg>
    ),
    'saving-calculator': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="tab-icon" aria-hidden="true">
        <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zM9 17H7v-2h2v2zm0-4H7v-2h2v2zm0-4H7V7h2v2zm4 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2zm4 8h-2v-4h2v4zm0-8h-2V7h2v2z"/>
      </svg>
    ),
    'meta-creative-settings': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="tab-icon" aria-hidden="true">
        <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9-4.03-9-9-9zm-1 14H9V7h2v10zm4 0h-2V7h2v10z" opacity=".3"/>
        <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
      </svg>
    ),
    'block-repository': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="tab-icon" aria-hidden="true">
        <path d="M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2z"/>
      </svg>
    ),
    'section-tags': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="tab-icon" aria-hidden="true">
        <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58s1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41s-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"/>
      </svg>
    ),
    'groups': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="tab-icon" aria-hidden="true">
        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
      </svg>
    ),
    'setup': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="tab-icon" aria-hidden="true">
        <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
      </svg>
    ),
    'flows-setup': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="tab-icon" aria-hidden="true">
        <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z"/>
      </svg>
    ),
    'billing-payment': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="tab-icon" aria-hidden="true">
        <path d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
      </svg>
    ),
    'support': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tab-icon" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    ),
    'form-settings': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="tab-icon" aria-hidden="true">
        <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm-7 3a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm6 12H6v-1c0-2 4-3.1 6-3.1s6 1.1 6 3.1v1z"/>
      </svg>
    ),
    'youtube-integration': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="tab-icon" aria-hidden="true">
        <path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/>
      </svg>
    ),
    'appearance': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="tab-icon" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
        <path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor" />
      </svg>
    ),
    'sign-out': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="tab-icon" aria-hidden="true">
        <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
      </svg>
    ),
    'mcp': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tab-icon" aria-hidden="true">
        <rect x="4" y="4" width="7" height="7" rx="1.5"/>
        <rect x="13" y="13" width="7" height="7" rx="1.5"/>
        <path d="M11 7.5h4a2 2 0 0 1 2 2V13M7.5 11v4a2 2 0 0 0 2 2H13"/>
      </svg>
    ),
  };

  // Debug logging (commented out to prevent infinite re-rendering)
  // console.log('Allowed pages for store:', allowedPagesForStore);
  // console.log('All settings sections:', settingsSections);
  // console.log('Filtered sections:', filteredSections);

  return (
    <div className='main-container'>
      {!shop_domain ? (
        <div className='go_to_shopify_admin_div'>
          <h5 style={{ display: 'inline-block' }}>!! Go to Shopify Admin and open our app !!</h5>
        </div>
      ) : (
        <>
          {loading ? (
            <div className='main_conatiner_spinner'>
              <Spinner animation="border" />
            </div>
          ) : (
            <Container fluid className="p-0">
              <div className='opportunities-header main-title'>
                <h2>Account Settings</h2>
              </div>

              <div className="tab-container mt-4">
                <div className="tab-navigation">
                  {groupSections.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      className={`tab-button ${effectiveActiveTab === section.id ? 'active' : ''}`}
                      onClick={() => handleTabChange(section.id)}
                    >
                      {TAB_ICONS[section.id]}
                      {section.title}
                    </button>
                  ))}
                </div>

                <div className="tab-content settings-content">
                  {filteredSections.filter((section) => section.group !== 'teams-workspace' && isSectionMounted(section)).map((section) => (
                    <div
                      key={section.id}
                      className={`settings-tab-content ${effectiveActiveTab === section.id ? 'active' : 'hidden'}`}
                    >
                      {React.createElement(section.component, {
                        ViewedPage: ViewedPage,
                        shop_domain: shop_domain,
                        app_url: app_url,
                        isInSettingsContainer: true
                      })}
                    </div>
                  ))}
                  {/* The "Workspace" and "Teams" tabs share ONE CF provider + Team page
                      (mounted once); the active tab selects which view to render. */}
                  {visibleGroups.has('teams-workspace') && (
                    <div className={`settings-tab-content ${(effectiveActiveTab === 'workspace' || effectiveActiveTab === 'teams') ? 'active' : 'hidden'}`}>
                      <TeamWorkspaceSettings app_url={app_url} view={effectiveActiveTab === 'teams' ? 'teams' : 'workspace'} />
                    </div>
                  )}
                </div>
              </div>
            </Container>
          )}
        </>
      )}
    </div>
  );
};

export default Settings; 